<template>
  <div class="turnstile-widget">
    <div ref="container" class="turnstile-container"></div>
    <p v-if="loadError" class="turnstile-error">
      Couldn’t load the security check. Refresh the page or disable any script blockers, then try again.
    </p>
  </div>
</template>

<script>
import { TURNSTILE_SITE_KEY } from '../../config'

// Load the Turnstile script once per page, regardless of how many widgets mount.
let scriptPromise = null
function loadTurnstileScript() {
  if (typeof window !== 'undefined' && window.turnstile) {
    return Promise.resolve()
  }
  if (scriptPromise) {
    return scriptPromise
  }
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('Failed to load Cloudflare Turnstile'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

export default {
  name: 'TurnstileWidget',
  props: {
    // Distinguishes the protected action in Cloudflare analytics (e.g. 'login' / 'register').
    action: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: 'dark',
    },
    size: {
      type: String,
      default: 'normal',
    },
  },
  data() {
    return {
      widgetId: null,
      loadError: false,
    }
  },
  mounted() {
    loadTurnstileScript()
      .then(() => this.renderWidget())
      .catch(() => {
        this.loadError = true
        this.$emit('error')
      })
  },
  beforeDestroy() {
    this.removeWidget()
  },
  methods: {
    renderWidget() {
      if (!window.turnstile || !this.$refs.container) return
      this.widgetId = window.turnstile.render(this.$refs.container, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: this.theme,
        size: this.size,
        action: this.action || undefined,
        callback: (token) => this.$emit('verified', token),
        'error-callback': () => {
          this.$emit('error')
        },
        'expired-callback': () => {
          this.$emit('expired')
        },
        'timeout-callback': () => {
          this.$emit('expired')
        },
      })
    },
    // Single-use tokens: call after a submit (or when switching login/register) to get a fresh one.
    reset() {
      if (window.turnstile && this.widgetId !== null) {
        window.turnstile.reset(this.widgetId)
      }
    },
    removeWidget() {
      if (window.turnstile && this.widgetId !== null && window.turnstile.remove) {
        window.turnstile.remove(this.widgetId)
      }
      this.widgetId = null
    },
  },
}
</script>

<style scoped>
.turnstile-widget {
  display: grid;
  gap: 0.4rem;
}

.turnstile-container {
  min-height: 65px;
}

.turnstile-error {
  margin: 0;
  color: #f08a95;
  font-size: 11.5px;
  font-weight: 650;
  line-height: 1.35;
}
</style>
